import { create } from 'zustand';
import { DateRange } from 'react-day-picker';
import { ColumnState } from 'ag-grid-community';

import { defaultPRD1010_R01Dto, PRD1010_R01Dto } from './Dto_PRD1010_R01';
import { formatDateMonth, formatDateYear4 } from 'src/utils/string/stringUtil';
import { showWarning } from 'src/utils/toast/toastUtil';

export interface PRD1010_R01Filter {
	compCd: string;
	yearMonth: string;
}

export interface Store_PRD1010_R01 {
	PRD1010_R01Filter: PRD1010_R01Filter;
	PRD1010_R01PastFilter: PRD1010_R01Filter;
	setPRD1010_R01Filter: (filter: Partial<PRD1010_R01Filter>) => void;
	setPRD1010_R01PastFilter: (filter: Partial<PRD1010_R01Filter>) => void;

	PRD1010_R01List: PRD1010_R01Dto[];
	setPRD1010_R01List: (PRD1010_R01List: PRD1010_R01Dto[]) => void;
	insertPRD1010_R01List: (data: PRD1010_R01Dto[], index?: number) => void;
	removePRD1010_R01List: (index: number) => void;
	updatePRD1010_R01List: (index: number, PRD1010_R01List: PRD1010_R01Dto) => void;

	deletedPRD1010_R01List: string[];
	addDeletedPRD1010_R01List: (idList: string[]) => void;
	removeDeletedPRD1010_R01List: (idList?: string[]) => void;

	selectedPRD1010_R01List: string[];
	setSelectedPRD1010_R01List: (idList: string[]) => void;
	addSelectedPRD1010_R01List: (idList: string[]) => void;
	removeSelectedPRD1010_R01List: (idList?: string[]) => void;
	initSelectedPRD1010_R01List: () => void;
	allSelectedPRD1010_R01List: () => void;

	originalPRD1010_R01List: PRD1010_R01Dto[];
	setOriginalPRD1010_R01List: (list: PRD1010_R01Dto[]) => void;

	columnState: ColumnState[] | null;
	setColumnState: (state: ColumnState[] | null) => void;

	allCount: number;					setAllCount: (allCount: number) => void;

	scrollTop: number | null;
	setScrollTop: (top: number | null) => void;
	

	initData: () => void;

}

export interface Store_PRD1010_R01_GridCalendarState {

	// ====== PRD1010_R01 GridCalendar 상태 ======
	PRD1010_R01GridCalendarState: {
		pickerVisible: boolean;
		pickerPosition: { top: number; left: number };
		targetRowIndex: number | null;
		targetField: string | null;
		targetDate: Date | undefined;
	};
	setPRD1010_R01GridCalendarState: (state: Partial<Store_PRD1010_R01_GridCalendarState['PRD1010_R01GridCalendarState']>) => void;
	initPRD1010_R01GridCalendarState: () => void;

}

export const defaultPRD1010_R01Filter: PRD1010_R01Filter = {
	compCd: '',
	yearMonth: formatDateYear4(new Date())+'-'+formatDateMonth(new Date())
};

export const usePRD1010_R01Store = create<Store_PRD1010_R01>((set) => ({
	PRD1010_R01Filter: defaultPRD1010_R01Filter,
	PRD1010_R01PastFilter: defaultPRD1010_R01Filter,
	setPRD1010_R01Filter: (update) => set((state) => ({
		PRD1010_R01Filter: {
			...state.PRD1010_R01Filter,
			...update
		}
	})),
	setPRD1010_R01PastFilter: (update) => set((state) => ({
		PRD1010_R01PastFilter: {
			...state.PRD1010_R01PastFilter,
			...update
		}
	})),

	PRD1010_R01List: [],
	setPRD1010_R01List: (PRD1010_R01List: PRD1010_R01Dto[]) => set((state) => {
		let newList: PRD1010_R01Dto[] = [];
		let valueAndOrderIndex = 1;
		PRD1010_R01List.forEach((item, index) => {
			// console.log(index + '번째 행의 item.Index 정보: ' 
			// 	+ ' valueIndex : ' + item.valueIndex 
			// 	+ ' valueAndOrderIndex : ' + valueAndOrderIndex
			// 	+ ' orderIndex : ' + item.orderIndex 
			// 	+ ' orderIndexSeq : ' + item.orderIndexSeq);
			newList.push({ 
				...defaultPRD1010_R01Dto,
				...item,
				valueIndex: item.valueIndex > 0 ? item.valueIndex : valueAndOrderIndex,
				orderIndex: item.orderIndex > -1 ? item.orderIndex : valueAndOrderIndex,
				orderIndexSeq: item.orderIndexSeq > 0 ? item.orderIndexSeq : 0
			});
			valueAndOrderIndex++;
		});
		return {
			...state,
			PRD1010_R01List: newList
		}
	}),
	insertPRD1010_R01List: (data: PRD1010_R01Dto[], index?: number) => set((state) => {
		let newPRD1010_R01List = [...state.PRD1010_R01List];

		let targetRowData = newPRD1010_R01List.find(item => item.valueIndex === index);
		let targetOrderIndex = targetRowData?.orderIndex || 0;
		// 데이터에 orderIndex가 있으면 그것을 사용하고, 없으면 1 (index 파라미터는 배열 위치로만 사용)		

		const newOrderIndexSeq = (targetRowData?.orderIndexSeq || 0) + 1;
		// console.log('targetOrderIndex : ', targetOrderIndex);
		// console.log('newOrderIndexSeq : ', newOrderIndexSeq);
		let maxValueIndex = Math.max(0, ...newPRD1010_R01List.map(item => item.valueIndex || 0)) + 1;

		// newSYS4100_R01List에서 targetOrderIndex인 행들을 빼내서 newOrderIndexSeq 기준으로 다시 정렬
		// newOrderIndexSeq 보다 orderIndexSeq가 작은 행들은 유지. 
		// newOrderIndexSeq 보다 orderIndexSeq가 큰 행들은 기존 orderIndexSeq 값에 1을 더해서 저장.
		const targetRows = newPRD1010_R01List.filter(item => item.orderIndex === targetOrderIndex);
		targetRows.forEach(item => {
			if(item.orderIndexSeq < newOrderIndexSeq) {
				item.orderIndexSeq = item.orderIndexSeq;
			} else if(item.orderIndexSeq >= newOrderIndexSeq) {
				item.orderIndexSeq = item.orderIndexSeq + 1;
			}
		});

		// targetRows를 newSYS4100_R01List에 대체
		newPRD1010_R01List = newPRD1010_R01List.filter(item => item.orderIndex !== targetOrderIndex);
		newPRD1010_R01List.push(...targetRows);
		
		//새 행을 생성해서 newOrderIndexSeq 값의 행을 저장
		const newData = data.map(item => ({ ...defaultPRD1010_R01Dto, ...item, 
			valueIndex: maxValueIndex++,
			orderIndex: targetOrderIndex,
			orderIndexSeq: newOrderIndexSeq
		}));
		if (index !== undefined) {
			newPRD1010_R01List.splice(index, 0, ...newData);
		} else {
			newPRD1010_R01List.push(...newData);
		}

		return {
			...state,
			PRD1010_R01List: newPRD1010_R01List
		}
	}),
	removePRD1010_R01List: (index: number) => set((state) => {
		const newPRD1010_R01List = [...state.PRD1010_R01List].filter((item) => item.valueIndex !== index);
		return {
			...state,
			PRD1010_R01List: newPRD1010_R01List
		}
	}),
	updatePRD1010_R01List: (valueIndex: number, data: PRD1010_R01Dto) => set((state) => {
		const _PRD1010_R01List = state.PRD1010_R01List.map(item =>
			item.valueIndex === valueIndex ? data : item
		);
		if(data.planQty < 0) {
			showWarning('수량은 0보다 작을 수 없습니다.');
			data.planQty = 0;
		}
		if(data.orderQty < 0) {
			showWarning('수량은 0보다 작을 수 없습니다.');
			data.orderQty = 0;
		}
		return {
			...state,
			PRD1010_R01List: _PRD1010_R01List
		}
	}),


	deletedPRD1010_R01List: [],
	addDeletedPRD1010_R01List: (idList: string[]) => set((state) => {
		const _deletedPRD1010_R01List = [...state.deletedPRD1010_R01List];

		for(const valueIndex of idList) {
			const isInserted = state.PRD1010_R01List.find(item => item.valueIndex === Number(valueIndex) && item.inserted);
			const isModified = state.PRD1010_R01List.find(item => item.valueIndex === Number(valueIndex) && item.isModified);
			if(!isInserted && !isModified) {
				_deletedPRD1010_R01List.push(valueIndex);
			}
		}
		return {
			...state,
			deletedPRD1010_R01List: _deletedPRD1010_R01List
		}
	}),
	removeDeletedPRD1010_R01List: (idList?: string[]) => set((state) => ({
		...state,
		deletedPRD1010_R01List: [...state.deletedPRD1010_R01List].filter(value => idList ? !idList.includes(value) : false)
	})),

	selectedPRD1010_R01List: [],
	setSelectedPRD1010_R01List: (idList: string[]) => set({ selectedPRD1010_R01List: idList }),
	addSelectedPRD1010_R01List: (idList: string[]) => set((state) => {
		const _selectedPRD1010_R01List = [...state.selectedPRD1010_R01List];
		if(idList.length > 0) {
			_selectedPRD1010_R01List.push(...idList);
		}
		return {
			...state,
			selectedPRD1010_R01List: _selectedPRD1010_R01List
		}
	}),
	removeSelectedPRD1010_R01List: (idList?: string[]) => set((state) => ({
		...state,
		selectedPRD1010_R01List: [...state.selectedPRD1010_R01List].filter(value => idList ? !idList.includes(value) : false)
	})),
	initSelectedPRD1010_R01List: () => set({ selectedPRD1010_R01List: [] }),
	allSelectedPRD1010_R01List: () => set((state) => {
		const _selectedPRD1010_R01List = [...state.PRD1010_R01List.map(item => item.valueIndex.toString())];
		return {
			...state,
			selectedPRD1010_R01List: _selectedPRD1010_R01List
		}
	}),

	originalPRD1010_R01List: [],
	setOriginalPRD1010_R01List: (PRD1010_R01List: PRD1010_R01Dto[]) => set((state) => {
		let newList: PRD1010_R01Dto[] = [];
		let valueAndOrderIndex = 1;
		PRD1010_R01List.forEach((item, index) => {
			newList.push({ 
				...defaultPRD1010_R01Dto,
				...item,
				valueIndex: valueAndOrderIndex,
				orderIndex: valueAndOrderIndex,
				orderIndexSeq: valueAndOrderIndex
			});
			valueAndOrderIndex++;
		});
		return {
			...state,
			originalPRD1010_R01List: newList
		}
	}),

	columnState: null,
	setColumnState: (state: ColumnState[] | null) => set({ columnState: state }),

	allCount: 0,					setAllCount: (allCount: number) => set({ allCount }),

	scrollTop: null,
	setScrollTop: (top: number | null) => set({ scrollTop: top }),
						

	initData: () => set((state) => ({
		...state,
		PRD1010_R01Filter: defaultPRD1010_R01Filter,
		PRD1010_R01PastFilter: defaultPRD1010_R01Filter,
		PRD1010_R01List: [],
		selectedPRD1010_R01List: [],
		deletedPRD1010_R01List: [],
		allCount: 0,
		scrollTop: null,
	})),

}));


export const usePRD1010_R01Store_GridCalendar = create<Store_PRD1010_R01_GridCalendarState>((set) => ({

	// ====== GridCalendar 상태 ======
	PRD1010_R01GridCalendarState: {
		pickerVisible: false,
		pickerPosition: { top: 0, left: 0 },
		targetRowIndex: null,
		targetField: null,
		targetDate: undefined
	},
	setPRD1010_R01GridCalendarState: (newState: Partial<Store_PRD1010_R01_GridCalendarState['PRD1010_R01GridCalendarState']>) => set((state) => ({
		...state,
		PRD1010_R01GridCalendarState: { ...state.PRD1010_R01GridCalendarState, ...newState }
	})),
	initPRD1010_R01GridCalendarState: () => set((state) => ({
		...state,
		PRD1010_R01GridCalendarState: {
			pickerVisible: false,
			pickerPosition: { top: 0, left: 0 },
			targetRowIndex: null,
			targetField: null,
			targetDate: undefined
		}
	})),


})); 